export const addOrReplace = (state, newElement, idField = 'id') => {
    const newId = Number(newElement[idField]);
    const index = state.findIndex((element) => Number(element[idField]) === newId);
    if (-1 < index) {
        return state.map((element, i) => (i === index) ? newElement : element);
    } else {
        return [
            newElement,
            ...(state.filter((e, i) => i !== index))
        ];
    }
}

export const idAsNumber = (({id, ...props}) => ({ id: Number(id), ...props }));


export const normaliseTree = (state, entity, childrenField = 'children', aggregateField = 'count', idField = 'id') => {
    const normalised = [
        ...state.filter((el) => el[idField] !== entity[idField]),
        {
            ...entity,
            [childrenField]: entity[childrenField].map((child) => child[idField]),
            [aggregateField]: entity[childrenField].reduce((count, child) => count + Number(child[aggregateField]), Number(entity[aggregateField]))
        }
    ];

    return !entity[childrenField] ? normalised : entity[childrenField].reduce((prev, curr) => normaliseTree(prev, curr, childrenField, aggregateField, idField), normalised);
};

export const getTreeFromNormalised = (state, id, childrenField = 'children', idField = 'id') => {
    const entity = state.find((el) => el[idField] === id);
    if (!entity || !childrenField || !entity[childrenField]) { return entity; }
    return {
        ...entity,
        [childrenField]: entity[childrenField].map((id) => getTreeFromNormalised(state, id, childrenField, idField))
    };
};



//Appraisal reducer functions:

export const addIoItem = (state, action, itemTypes = []) => {
    if (!action.payload?.appraisalId) { return state; }
    const appraisal = state.find(({id}) => Number(id) === Number(action.payload.appraisalId));
    if (!appraisal) { return state; }
    const inputs = appraisal.inputs.segments.filter(({type}) => itemTypes.includes(type));
    const outputs = appraisal.outputs.segments.filter(({type}) => itemTypes.includes(type));
    if (!inputs && !outputs) { return state; }

    return [
        ...state.filter(({id}) => Number(id) !== Number(action.payload.appraisalId)),
        {
            ...appraisal,
            inputs: !inputs ? appraisal.inputs : {
                ...appraisal.inputs,
                segments: [
                    ...appraisal.inputs.segments.filter(({type}) => !itemTypes.includes(type)),
                    ...inputs.map((input) => (
                        {
                            ...input,
                            items: [
                                ...input.items,
                                action.data
                            ]
                        }
                    ))
                ]
            },
            outputs: !outputs ? appraisal.outputs : {
                ...appraisal.outputs,
                segments: [
                    ...appraisal.outputs.segments.filter(({type}) => !itemTypes.includes(type)),
                    ...outputs.map((output) => (
                        {
                            ...output,
                            items: [
                                ...output.items,
                                action.data
                            ]
                        }
                    ))
                ]
            }
        }
    ];
};

export const updateIoItem = (appraisal, ioTypes, itemId, changes = null) => {
    itemId = Number(itemId);
    const inputs = appraisal.inputs?.segments.filter((seg) => (
        ioTypes.includes(seg.type) && seg.items?.map(({id}) => Number(id)).includes(itemId)
    ));
    const outputs = appraisal.outputs?.segments.filter((seg) => (
        ioTypes.includes(seg.type) && seg.items?.map(({id}) => Number(id)).includes(itemId)
    ));
    if (!inputs?.length && !outputs?.length) { return appraisal; }

    return {
        ...appraisal,
        inputs: !inputs ? appraisal.inputs : {
            ...appraisal.inputs,
            segments: [
                ...appraisal.inputs.segments.filter((seg) => !(inputs.map(({index}) => index).includes(seg.index))),
                ...inputs.map((input) => ({
                    ...input,
                    items: !changes
                        ? input.items.filter(({id}) => Number(id) !== itemId)
                        : [
                            ...input.items.filter(({id}) => Number(id) !== itemId),
                            {
                                ...input.items.find(({id}) => Number(id) === itemId),
                                ...changes
                            }
                        ]
                }))
            ]
        },
        outputs: !outputs ? appraisal.outputs : {
            ...appraisal.outputs,
            segments: [
                ...appraisal.outputs.segments.filter((seg) => !(outputs.map(({index}) => index).includes(seg.index))),
                ...outputs.map((output) => ({
                    ...output,
                    items: !changes
                        ? output.items.filter(({id}) => Number(id) !== itemId)
                        : [
                            ...output.items.filter(({id}) => Number(id) !== itemId),
                            {
                                ...output.items.find(({id}) => Number(id) === itemId),
                                ...changes
                            }
                        ]
                }))
            ]
        },
    };
};


const updateElement = (currentElement, updatedElement) => {
    if (currentElement.name === updatedElement.name) { return { ...currentElement, ...updatedElement }; }
    if (!currentElement.children) { return currentElement; }
    return {
        ...currentElement,
        children: currentElement.children.map((child) => updateElement(child, updatedElement))
    };
};

export const updateFormElement = (state, updatedElement, appraisal) => {
    const getElementNames = (element) => [element.name, ...(element.children || []).reduce((acc, curr) => [...acc, ...getElementNames(curr)], [])];
    const getFormElementNames = (elements) => elements.reduce((acc, curr) => [...acc, ...getElementNames(curr)], []);
    const input = appraisal.inputs?.segments?.find((el) => !!el.form?.elements && getFormElementNames(el.form.elements).includes(updatedElement.name));
    const output = appraisal.outputs?.segments?.find((el) => !!el.form?.elements && getFormElementNames(el.form.elements).includes(updatedElement.name));
    if (!input && !output) { return state; }

    return [
        ...state.filter(({id}) => Number(id) !== Number(appraisal.id)),
        {
            ...appraisal,
            inputs: !input ? appraisal.inputs : {
                ...appraisal.inputs,
                segments: [
                    ...appraisal.inputs.segments.filter(({index}) => index !== input.index),
                    {
                        ...appraisal.inputs.segments.find(({index}) => index === input.index),
                        form: {
                            ...input.form,
                            elements: input.form.elements.map((element) => updateElement(element, updatedElement))
                        },
                    }
                ]
            },
            outputs: !output ? appraisal.outputs : {
                ...appraisal.outputs,
                segments: [
                    ...appraisal.outputs.segments.filter(({index}) => index !== output.index),
                    {
                        ...appraisal.outputs.segments.find(({index}) => index === output.index),
                        form: {
                            ...output.form,
                            elements: output.form.elements.map((element) => updateElement(element, updatedElement))
                        },
                    }
                ]
            }
        }
    ];
};
